import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { areaElementClasses } from '@mui/x-charts/LineChart';

// Generate mock time series data - will be replaced with Firestore data
const generateMockData = (currentValue, min, max, points = 30) => {
  const data = [];
  const range = max - min;

  for (let i = points - 1; i >= 0; i--) {
    // Generate realistic fluctuating values around current value
    const variation = (Math.random() - 0.5) * range * 0.15;
    const value = Math.max(min, Math.min(max, currentValue + variation));
    data.push(Number(value.toFixed(2)));
  }
  // Ensure last point is current value
  data[data.length - 1] = currentValue;
  return data;
};

// Gradient component for area fill - like StatCard
function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.5} />
        <stop offset="100%" stopColor={color} stopOpacity={0.05} />
      </linearGradient>
    </defs>
  );
}

function TimeSeriesPlot({
  value,
  min,
  max,
  optimalMin,
  optimalMax,
  size = 140,
  color = '#22c55e',
  fullWidth = false,
}) {
  // Generate mock data - in production this comes from Firestore
  const data = React.useMemo(() => generateMockData(value, min, max), [value, min, max]);

  // Determine line color based on current value
  const getLineColor = () => {
    if (value >= optimalMin && value <= optimalMax) return '#22c55e';
    if (value < optimalMin) {
      const diff = optimalMin - value;
      const threshold = (optimalMin - min) * 0.1;
      return diff > threshold ? '#ef4444' : '#eab308';
    }
    const diff = value - optimalMax;
    const threshold = (max - optimalMax) * 0.1;
    return diff > threshold ? '#ef4444' : '#eab308';
  };

  const lineColor = color || getLineColor();

  // Create unique gradient ID based on value to prevent conflicts
  const gradientId = `area-gradient-${Math.random().toString(36).substr(2, 9)}`;

  // Chart dimensions - sparkline style (full width, compact height)
  const chartHeight = fullWidth ? size * 0.6 : size * 0.5;

  return (
    <Box sx={{
      width: '100%',
      height: chartHeight,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      px: 1,
    }}>
      <SparkLineChart
        data={data}
        height={chartHeight}
        curve="natural"
        area
        showHighlight
        showTooltip
        colors={[lineColor]}
        sx={{
          [`& .${areaElementClasses.root}`]: {
            fill: `url(#${gradientId})`,
          },
          '& .MuiLineElement-root': {
            strokeWidth: 2,
          },
        }}
      >
        <AreaGradient color={lineColor} id={gradientId} />
      </SparkLineChart>
    </Box>
  );
}

TimeSeriesPlot.propTypes = {
  value: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  optimalMin: PropTypes.number,
  optimalMax: PropTypes.number,
  size: PropTypes.number,
  color: PropTypes.string,
  fullWidth: PropTypes.bool,
};

export default TimeSeriesPlot;
